import React, { useEffect, useState } from "react";
import {
  FaBoxOpen,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSearch,
  FaStar,
  FaTimes,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import {
  getProducts,
  getCategories,
  addProduct,
  updateProductApi,
  deleteProductApi,
} from "../services/api";

import { useAuthStore } from "../store/authStore";

import Layout from "./Layout";
import ProfileSidebar from "./ProfileSidebar";

export default function AllProducts() {
  const navigate = useNavigate();

  const user = useAuthStore((state) => state.user);

  // =====================================================
  // ADMIN CHECK
  // =====================================================

  const ADMIN_ID = "6a86fc0273e1ca02be82c099";

  const isAdmin = user?.id === ADMIN_ID;

  // =====================================================
  // STATE
  // =====================================================

  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState();
  const [categories, setCategories] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 20;

  const [productForm, setProductForm] = useState({
    name: "",
    description: "",
    price: "",
    categoryId: "",
    image: "",
    qty: "",
    featured: false,
  });

  // =====================================================
  // FETCH PRODUCTS
  // =====================================================

  const fetchProducts = async (category = "", search = "", page = 1) => {
    try {
      setLoading(true);
      setError("");

      const limit = productsPerPage;
      const skip = (page - 1) * limit;

      //const res = await api.get("/products", { params });

      const response = await getProducts(category, skip, limit, search);

      console.log("PRODUCTS RESPONSE:", response, search);

      if (response?.items) {
        setProducts(response.items);
        setTotal(response.total);
      } else if (Array.isArray(response)) {
        setProducts(response);
      } else {
        setProducts([]);
      }
    } catch (err) {
      console.error("Fetch products error:", err);

      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to load products.",
      );
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // FETCH CATEGORIES
  // =====================================================

  const fetchCategories = async () => {
    try {
      const response = await getCategories();

      console.log("CATEGORIES RESPONSE:", response);

      if (Array.isArray(response)) {
        setCategories(response);
      } else if (response?.items) {
        setCategories(response.items);
      } else if (response?.categories) {
        setCategories(response.categories);
      }
    } catch (err) {
      console.error("Fetch categories error:", err);
    }
  };

  // =====================================================
  // INITIAL LOAD
  // =====================================================

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }

    fetchCategories();
  }, [isAdmin]);

  // =====================================================
  // FETCH PRODUCTS WHEN FILTER CHANGES
  // =====================================================

  // =====================================================
  // SEARCH
  // =====================================================

  useEffect(() => {
    if (!isAdmin) return;

    const timer = setTimeout(() => {
      fetchProducts(selectedCategory, search, currentPage);
    }, 400);

    return () => clearTimeout(timer);
  }, [isAdmin, selectedCategory, search, currentPage]);

  // =====================================================
  // HELPERS
  // =====================================================

  const getCategoryName = (product) => {
    return (
      product?.category?.name ||
      categories.find(
        (category) => String(category._id) === String(product.categoryId),
      )?.name ||
      "-"
    );
  };

  const formatAmount = (amount) => {
    return Number(amount || 0).toLocaleString("en-IN");
  };

  const getProductImage = (product) => {
    if (product?.image) return product.image;

    if (product?.images?.length > 0) {
      return product.images[0];
    }

    return null;
  };

  // =====================================================
  // FORM
  // =====================================================

  const resetForm = () => {
    setProductForm({
      name: "",
      description: "",
      price: "",
      categoryId: "",
      image: "",
      qty: "",
      featured: false,
    });

    setEditingProduct(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);

    setProductForm({
      name: product.name || "",
      description: product.description || "",
      price: product.price || "",
      categoryId: product.categoryId || "",
      image: Array.isArray(product.image)
        ? product.image[0] || ""
        : product.image || "",
      qty: product.qty || "",
      featured: product.featured || false,
    });

    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;

    setShowModal(false);
    resetForm();
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;

    setProductForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // =====================================================
  // SAVE PRODUCT
  // =====================================================

  const handleSaveProduct = async (e) => {
    e.preventDefault();

    if (!productForm.name.trim()) {
      alert("Product name is required.");
      return;
    }

    if (!productForm.categoryId) {
      alert("Please select a category.");
      return;
    }

    if (!productForm.price) {
      alert("Product price is required.");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        name: productForm.name.trim(),
        description: productForm.description.trim(),
        price: Number(productForm.price),
        categoryId: productForm.categoryId,
        //image: productForm.image.trim(),
        qty: Number(productForm.qty || 0),
        featured: productForm.featured,
      };

      if (editingProduct) {
        await updateProductApi(editingProduct._id, payload);

        alert("Product updated successfully.");
      } else {
        await addProduct(payload);

        alert("Product added successfully.");
      }

      closeModal();

      await fetchProducts();
    } catch (err) {
      console.error("Save product error:", err);

      alert(
        err.response?.data?.message || err.message || "Failed to save product.",
      );
    } finally {
      setSaving(false);
    }
  };

  // =====================================================
  // DELETE PRODUCT
  // =====================================================

  const handleDeleteProduct = async (product) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${product.name}"?`,
    );

    if (!confirmed) return;

    try {
      await deleteProductApi(product._id);

      setProducts((prev) => prev.filter((item) => item._id !== product._id));

      alert("Product deleted successfully.");
    } catch (err) {
      console.error("Delete product error:", err);

      alert(
        err.response?.data?.message ||
          err.message ||
          "Failed to delete product.",
      );
    }
  };

  // =====================================================
  // UNAUTHORIZED
  // =====================================================

  if (!isAdmin) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-6 py-20">
          <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-6 text-center">
            <h2 className="text-xl font-semibold mb-2">Access Denied</h2>

            <p>You are not authorized to manage products.</p>

            <button
              type="button"
              onClick={() => navigate("/")}
              className="mt-5 bg-light_primary hover:bg-hover_light_primary text-white px-6 py-3 rounded-lg transition"
            >
              Back to Home
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <Layout>
      {/* =====================================================
          HEADER
      ===================================================== */}

      <section className="bg-[#FFF8EC] px-6 md:px-16 py-8">
        <div className="mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center">
                <FaBoxOpen className="text-2xl text-[#FF945B]" />
              </div>

              <div>
                <h2 className="text-3xl md:text-4xl font-serif text-light_primary">
                  Products
                </h2>

                <p className="text-gray-500 mt-1">
                  Manage all Legacy Decor products
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={openAddModal}
              className="flex items-center justify-center gap-2 bg-light_primary hover:bg-hover_light_primary text-white px-6 py-3 rounded-lg transition shadow-sm"
            >
              <FaPlus />
              Add Product
            </button>
          </div>
        </div>
      </section>

      {/* =====================================================
          CONTENT
      ===================================================== */}

      <section className="px-6 lg:px-12 md:px-12 sm:px-12 xs:px-10 xxs:px-8 py-6 md:py-10 lg:py-10 sm:py-10 xs:py-8 xxs:py-6 mx-auto bg-white">
        <div className="mx-auto grid grid-cols-1 md:grid-cols-[270px_1fr] gap-8">
          {/* SIDEBAR */}
          <ProfileSidebar />
          {/* PRODUCT CONTENT */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 md:p-8">
            {/* =================================================
                TOP CONTROLS
            ================================================= */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 border-b border-gray-200 pb-6 mb-6">
              {/* SEARCH */}

              <div className="relative w-full lg:max-w-md">
                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

                <input
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Search products..."
                  className="text-black w-full border border-gray-300 rounded-lg pl-11 pr-4 py-3 outline-none focus:border-light_primary"
                />
              </div>

              {/* CATEGORY */}

              <select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setCurrentPage(1);
                }}
                className="text-black border border-gray-300 rounded-lg px-4 py-3 outline-none focus:border-light_primary min-w-[220px]"
              >
                <option value="">All Categories</option>

                {categories.map((category) => (
                  <option key={category._id} value={category.slug}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            {/* =================================================
                SUMMARY
            ================================================= */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-semibold text-gray-900">
                  Product List
                </h3>

                <p className="text-sm text-gray-500 mt-1">
                  Add, edit or remove products from your store
                </p>
              </div>
              {products ? (
                <span className="bg-[#FFF3E5] text-[#E87932] px-4 py-2 rounded-full text-sm font-medium">
                  {total} Products
                </span>
              ) : (
                ""
              )}
            </div>
            {/* =================================================
                LOADING
            ================================================= */}
            {loading && (
              <div className="text-center py-20 text-gray-500">
                Loading products...
              </div>
            )}
            {/* =================================================
                ERROR
            ================================================= */}
            {!loading && error && (
              <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-4 text-center">
                {error}
              </div>
            )}
            {/* =================================================
                EMPTY
            ================================================= */}
            {!loading && !error && products.length === 0 && (
              <div className="p-10 text-center">
                <FaBoxOpen className="mx-auto text-5xl text-gray-300 mb-4" />

                <h2 className="text-2xl font-semibold text-gray-800">
                  No Products Found
                </h2>

                <p className="text-gray-500 mt-2">
                  Try changing the category or search.
                </p>

                <button
                  type="button"
                  onClick={openAddModal}
                  className="mt-5 bg-light_primary hover:bg-hover_light_primary text-white px-6 py-3 rounded-lg"
                >
                  <FaPlus className="inline mr-2" />
                  Add Product
                </button>
              </div>
            )}
            {/* =================================================
                TABLE
            ================================================= */}
            {!loading && !error && products.length > 0 && (
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                {/* HEADER */}

                <div className="hidden xl:grid grid-cols-[80px_1.6fr_1fr_120px_100px_100px_90px] gap-4 px-5 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase">
                  <div>Image</div>
                  <div>Product</div>
                  <div>Category</div>
                  <div>Price</div>
                  <div>Qty</div>
                  <div>Featured</div>
                  <div>Actions</div>
                </div>

                {/* ROWS */}
                {products.map((product) => {
                  const image = getProductImage(product);
                  return (
                    <div
                      key={product._id}
                      className="grid grid-cols-1 xl:grid-cols-[80px_1.6fr_1fr_120px_100px_100px_90px] gap-4 px-5 py-5 border-b border-gray-200 last:border-b-0 hover:bg-[#FFF8EC] transition items-center"
                    >
                      {/* IMAGE */}

                      <div>
                        {image ? (
                          <img
                            src={image[0]}
                            alt={product.name}
                            className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center">
                            <FaBoxOpen className="text-gray-400" />
                          </div>
                        )}
                      </div>

                      {/* PRODUCT */}

                      <div>
                        <p className="font-semibold text-gray-800">
                          {product.name}
                        </p>

                        {product.description && (
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                            {product.description}
                          </p>
                        )}
                      </div>

                      {/* CATEGORY */}

                      <div>
                        <p className="text-sm font-medium text-gray-700">
                          {getCategoryName(product)}
                        </p>
                      </div>

                      {/* PRICE */}

                      <div>
                        <p className="font-semibold text-light_primary">
                          ₹{formatAmount(product.price)}
                        </p>
                      </div>

                      {/* QTY */}

                      <div>
                        <span
                          className={`text-sm font-medium ${
                            Number(product.qty || 0) > 0
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                        >
                          {product.qty ?? 0}
                        </span>
                      </div>

                      {/* FEATURED */}

                      <div>
                        {product.featured ? (
                          <span className="inline-flex items-center gap-1 text-yellow-600 text-sm font-medium">
                            <FaStar />
                            Yes
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">No</span>
                        )}
                      </div>

                      {/* ACTIONS */}

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          title="Edit Product"
                          onClick={() => openEditModal(product)}
                          className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 flex items-center justify-center transition"
                        >
                          <FaEdit />
                        </button>

                        <button
                          type="button"
                          title="Delete Product"
                          onClick={() => handleDeleteProduct(product)}
                          className="w-9 h-9 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 flex items-center justify-center transition"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {/* PAGINATION */}
            {!loading && !error && total > productsPerPage && (
              <div className="flex items-center justify-between mt-6">
                <p className="text-sm text-gray-500">
                  Showing{" "}
                  <span className="font-medium text-gray-700">
                    {(currentPage - 1) * productsPerPage + 1}
                  </span>
                  {" - "}
                  <span className="font-medium text-gray-700">
                    {Math.min(currentPage * productsPerPage, total)}
                  </span>
                  {" of "}
                  <span className="font-medium text-gray-700">
                    {total}
                  </span>{" "}
                  products
                </p>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((prev) => prev - 1)}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>

                  <span className="px-4 py-2 rounded-lg bg-light_primary text-white">
                    {currentPage}
                  </span>

                  <button
                    type="button"
                    disabled={currentPage >= Math.ceil(total / productsPerPage)}
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}{" "}
          </div>
        </div>
      </section>

      {/* =====================================================
          ADD / EDIT MODAL
      ===================================================== */}

      {showModal && (
        <div className="text-black fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-xl">
            {/* MODAL HEADER */}

            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-semibold text-gray-900">
                  {editingProduct ? "Edit Product" : "Add Product"}
                </h2>

                <p className="text-sm text-gray-500 mt-1">
                  {editingProduct
                    ? "Update product information"
                    : "Add a new product to your store"}
                </p>
              </div>

              <button
                type="button"
                onClick={closeModal}
                disabled={saving}
                className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500"
              >
                <FaTimes />
              </button>
            </div>

            {/* FORM */}

            <form onSubmit={handleSaveProduct} className="p-6 space-y-5">
              {/* NAME */}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product Name *
                </label>

                <input
                  type="text"
                  name="name"
                  value={productForm.name}
                  onChange={handleFormChange}
                  placeholder="Enter product name"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:border-light_primary"
                />
              </div>

              {/* DESCRIPTION */}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>

                <textarea
                  name="description"
                  value={productForm.description}
                  onChange={handleFormChange}
                  rows="4"
                  placeholder="Enter product description"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:border-light_primary resize-none"
                />
              </div>

              {/* PRICE + QTY */}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price *
                  </label>

                  <input
                    type="number"
                    name="price"
                    value={productForm.price}
                    onChange={handleFormChange}
                    placeholder="Enter price"
                    min="0"
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:border-light_primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Qty
                  </label>

                  <input
                    type="number"
                    name="qty"
                    value={productForm.qty}
                    onChange={handleFormChange}
                    placeholder="Enter Qty"
                    min="0"
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:border-light_primary"
                  />
                </div>
              </div>

              {/* CATEGORY */}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>

                <select
                  name="categoryId"
                  value={productForm.categoryId}
                  onChange={handleFormChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:border-light_primary"
                >
                  <option value="">Select Category</option>

                  {categories.map((category) => (
                    <option key={category._id} value={category._id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* IMAGE */}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product Image URL
                </label>

                <input
                  type="text"
                  name="image"
                  value={productForm.image}
                  onChange={handleFormChange}
                  placeholder="https://..."
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:border-light_primary"
                />
              </div>

              {/* IMAGE PREVIEW */}

              {productForm.image && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Preview
                  </p>

                  <img
                    src={productForm.image}
                    alt="Preview"
                    className="w-32 h-32 object-cover rounded-lg border border-gray-200"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
              )}

              {/* FEATURED */}

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="featured"
                  checked={productForm.featured}
                  onChange={handleFormChange}
                  className="w-4 h-4 accent-orange-500"
                />

                <span className="text-sm font-medium text-gray-700">
                  Show this product as Featured
                </span>
              </label>

              {/* BUTTONS */}

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={saving}
                  className="px-5 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-3 rounded-lg bg-light_primary hover:bg-hover_light_primary text-white disabled:opacity-60"
                >
                  {saving
                    ? "Saving..."
                    : editingProduct
                      ? "Update Product"
                      : "Add Product"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
